import { notFound } from "@tanstack/react-router";
import type {
  PostDetailPublic,
  PostFeedPage,
} from "@softmax/observatory-client/types";
import { buildHref, matchRoute } from "@softmax/web-addresses";

import { readCommunity } from "../community.api";
import { type CommunityDestination, communityRoom } from "../community-room";

export async function loadForumPage({ href, signal }: CommunityDestination) {
  const room = await communityRoom({ href, signal });
  const result = await readCommunity<PostFeedPage>({
    signal,
    path: `/v2/forums/${encodeURIComponent(room.forumSlug)}/posts?sort=hot&limit=50`,
  });

  if (room.game && result.status === "not-found") throw notFound();

  const title = room.game
    ? `${room.displayName} Forum · Softmax`
    : "Forum · Softmax";
  return {
    room,
    page: result.status === "ok" ? result.data : null,
    metadata: {
      title,
      description: room.game
        ? `Discussion and updates from the ${room.displayName} community.`
        : "Softmax’s own discussion — every coworld, one room.",
      canonical: room.basePath,
      type: "website",
      index: room.indexable && result.status === "ok",
      follow: room.follow,
    },
  };
}

export async function loadForumPost({ href, signal }: CommunityDestination) {
  const route = matchRoute(new URL(href, window.location.origin).pathname);

  if (route.name !== "forumPost" && route.name !== "game.forumPost")
    throw notFound();

  const [room, result] = await Promise.all([
    communityRoom({ href, signal }),
    readCommunity<PostDetailPublic>({
      signal,
      path: `/v2/posts/${encodeURIComponent(route.postId)}`,
    }),
  ]);

  if (
    result.status === "not-found" ||
    (result.status === "ok" && result.data.post.deleted_at)
  ) {
    throw notFound();
  }

  const title =
    result.status === "ok"
      ? `${result.data.post.title} · ${room.game ? `${room.displayName} ` : ""}Forum · Softmax`
      : "Forum · Softmax";
  return {
    room,
    postId: route.postId,
    detail: result.status === "ok" ? result.data : null,
    metadata: {
      title,
      description: `Discussion from the ${room.displayName} community.`,
      canonical: room.game
        ? buildHref({
            name: "game.forumPost",
            game: room.game,
            postId: route.postId,
          })
        : buildHref({ name: "forumPost", postId: route.postId }),
      type: "article",
      index: room.indexable && result.status === "ok",
      follow: room.follow,
    },
  };
}
